import './mail-outgoing-selection-actions.ajscss';

import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { AbstractEpticaController } from '@agent/agent-interface/contact-view/mail/abstract-mail-process';
import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { EpticaHelper } from '@agent/core/channel-digit/mail/eptica/eptica.helper';
import { EpticaData } from '@agent/core/channel-digit/mail/eptica/models/eptica-data';
import {
  HOME_PAGE_NAME,
  IEpticaJsonResponse,
  IEpticaOutgoingFrame,
  REQUEST_CREATE_PAGE_NAME,
} from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { RecipientSearchType } from '@agent/core/channel-digit/mail/models/recipient';
import { IOutgoingMailDeprecatedService, OutgoingMailServiceMode } from '@agent/core/channel-digit/mail/outgoing-mail-deprecated.service';
import { isEmailValid } from '@agent/core/channel-digit/mail/util/mail-util';
import { ISmsDeprecatedService } from '@agent/core/channel-digit/sms/sms-deprecated.service';
import { IAppointmentDeprecatedService } from '@agent/core/contact/appointment-deprecated.service';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { IUnitOutgoingCallDeprecatedService } from '@agent/core/phone/outcall/unit-outgoing-call-deprecated.service';
import { ToastDeprecatedService } from '@commons/core/notification/toast-deprecated.service';
import { optToPromise } from '@commons/util/utilities/option';
import { destroy } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, ILogService, IPromise, IQService, ITimeoutService } from 'angular';
import { concatMap, delay, map, Observable, of, retryWhen, switchMap, take, throwError } from 'rxjs';

import {
  BookingFacade,
  Customer,
  filterOfArr,
  MailFacade,
  MailService,
  mapOfArr,
  Option,
  RefuseReason,
  TranslateService,
} from '@contact/upgrade';

import templateUrl from './mail-outgoing-selection-actions.template.html';
import { AgentBookinMailInprocessState } from './state/agent-booking-mail-routing.module';

const MAX_OUTGOING_DRAFT = 10;

class MailOutgoingSelectionActionsCtrl extends AbstractEpticaController<IEpticaOutgoingFrame> implements IComponentController {
  readonly AgentBookinMailInprocessState = AgentBookinMailInprocessState;
  override responseId: string;

  deferDisabled = false;
  private existingOutgoingDraftIds: string[] = []; // init here so when there is no draft no more error in getNewDraftId
  public maxOutgoingDraftNb = MAX_OUTGOING_DRAFT; // used in template only

  private allOutgoingDraftIds$ = this.mailService.getAllDrafts().pipe(
    take(1),
    filterOfArr(({ requestId }) => !requestId),
    mapOfArr(({ externalId }) => externalId)
  );

  constructor(
    override readonly $log: ILogService,
    override readonly $q: IQService,
    override readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly smsDeprecatedService: ISmsDeprecatedService,
    private readonly appointmentDeprecatedService: IAppointmentDeprecatedService,
    override readonly toastDeprecatedService: ToastDeprecatedService,
    private readonly outgoingMailDeprecatedService: IOutgoingMailDeprecatedService,
    private readonly unitOutgoingCallDeprecatedService: IUnitOutgoingCallDeprecatedService,
    private readonly $timeout: ITimeoutService,
    private readonly mailFacade: MailFacade,
    private readonly contactFacade: BookingFacade,
    private readonly mailService: MailService,
    override readonly router: AgentDesktopRouter,
    override readonly translateService: TranslateService
  ) {
    super($log, $q, contactDeprecatedService, toastDeprecatedService, router, translateService);
  }

  get selectedRecipientType(): RecipientSearchType {
    return this.outgoingMailDeprecatedService.getSelectedRecipientType();
  }

  get outcallRequested(): boolean {
    if (this.unitOutgoingCallDeprecatedService.unitOutgoingCall != null) {
      return this.unitOutgoingCallDeprecatedService.unitOutgoingCall.requested;
    }
    return false;
  }

  get canSendOutgoing() {
    switch (this.outgoingMailDeprecatedService.getSelectedRecipientType()) {
      case RecipientSearchType.BY_EMAIL:
        return isEmailValid(this.outgoingMailDeprecatedService.getEmailInput());
      case RecipientSearchType.BY_CONTACT_ID:
      case RecipientSearchType.BY_DIRECTORY:
      default:
        return false;
    }
  }

  get mode(): OutgoingMailServiceMode {
    return this.outgoingMailDeprecatedService.getMode();
  }

  get buttonsDisabled(): boolean {
    return !this.outgoingMailDeprecatedService.isEpticaFrameLoaded() || this.actionInProgress;
  }

  get actionInProgress(): boolean {
    return this.outgoingMailDeprecatedService.isActionInProgress();
  }

  set actionInProgress(actionInProgress: boolean) {
    this.outgoingMailDeprecatedService.setActionInProgress(actionInProgress);
  }

  override $onInit(): void {
    super.$onInit();
    this.actionInProgress = false;

    if (this.mainPromise() != null) {
      this.mainPromise().then(request => request.show());
    }

    this.allOutgoingDraftIds$.pipe(destroy(this)).subscribe(draftIds => {
      this.deferDisabled = draftIds.length >= MAX_OUTGOING_DRAFT;
      this.existingOutgoingDraftIds = draftIds;
      this.contactFacade
        .getById(this.responseId)
        .pipe(take(1), destroy(this))
        .subscribe(contact => {
          if (!!contact && !!contact.requestDraftId) {
            this.deferDisabled = false;
          }
        });
    });
  }

  $onDestroy(): void {
    if (this.mainPromise() != null) {
      this.mainPromise().then(request => request.hide());
    }
  }

  /**
   * Send the mail (submit eptica's form)
   */
  send(): void {
    this.actionInProgress = true;
    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      if (contact.customerId) {
        this.outgoingMailDeprecatedService.deleteOpenedFromInteraction(contact.customerId);
      }
    });

    // Add the CC and BCC recipients into contact
    if (this.mainPromise() != null) {
      this.mainPromise().then(request => request.addCcAndCccContacts());
    }

    let currentRequest: IEpticaOutgoingFrame;
    this.handleJsonAction(request => (currentRequest = request).send())
      .then(() => {
        if (currentRequest && currentRequest.getRealId().isDefined()) {
          this.contactDeprecatedService.getSelectedContact().then(contact => {
            this.epticaDeprecatedService.updateRequestFields(currentRequest.getRealId().get(), {
              status: 'ready_to_assign',
              perimeter: 'none',
              destination: contact.phoneNumber,
            });
          });
        }
      })
      .finally(() => (this.actionInProgress = false));
  }

  /**
   * Send the mail and postpone (submit eptica's form)
   */
  sendPostpone(): void {
    this.actionInProgress = true;

    let currentRequest: IEpticaOutgoingFrame;
    this.handleJsonAction(request => (currentRequest = request).sendPostpone())
      .then(() => {
        if (currentRequest && currentRequest.getRealId().isDefined()) {
          this.contactDeprecatedService.getSelectedContact().then(contact => {
            this.epticaDeprecatedService.updateRequestFields(currentRequest.getRealId().get(), {
              status: 'ready_to_assign',
              perimeter: 'none',
              destination: contact.phoneNumber,
            });
          });
        }
      })
      .finally(() => (this.actionInProgress = false));
  }

  /**
   * Defer
   */
  defer(): void {
    this.actionInProgress = true;
    this.handleJsonAction(
      request =>
        request.defer().then(data => {
          const deferred = this.$q.defer();
          this.contactFacade
            .getById(this.responseId)
            .pipe(
              take(1),
              switchMap(contact => {
                if (!contact) {
                  this.$log.warn(`contact not found for response id [${this.responseId}]`);
                  return throwError(() => '');
                }
                if (!contact.customerId) {
                  this.$log.warn(`customer not known for response id [${this.responseId}]`);
                  return throwError(() => '');
                }
                const customerId = contact.customerId;
                const draft$ = !!contact.requestDraftId ? of(contact.requestDraftId) : this.getNewDraftId(5, 2000);
                return draft$.pipe(
                  switchMap(
                    draftId =>
                      this.mailFacade.deferForNew(customerId, customerId, {
                        param: {
                          contactConnectionId: contact.connectionId,
                          data: {
                            comment: '',
                            bookmark: false,
                          },
                          draft: {
                            id: draftId,
                            message: undefined,
                          },
                        },
                      }).afterEvent$
                  )
                );
              })
            )
            .subscribe({
              error: () => deferred.reject(),
              complete: () => deferred.resolve(),
            });
          return deferred.promise.then(() => data);
        }),
      RefuseReason.DEFER
    ).finally(() => (this.actionInProgress = false));
  }

  private getNewDraftId(nbRetry: number, delayBetweenRetryMs: number): Observable<string> {
    return this.allOutgoingDraftIds$.pipe(
      map(ids => {
        return ids.find(id => !this.existingOutgoingDraftIds.includes(id));
      }),
      switchMap(newDraftId => {
        return newDraftId ? of(newDraftId) : throwError(() => 'draftId not available');
      }),
      retryWhen(err =>
        err.pipe(
          delay(delayBetweenRetryMs),
          take(nbRetry),
          concatMap(() => throwError(() => `draftId not available after ${nbRetry} retries`))
        )
      )
    );
  }

  /**
   * Open the popin that allow the csr the search for pre-answered response
   */
  browseKnowledge(): void {
    this.doJsAction(['..', AgentBookinMailInprocessState.knowledge], request =>
      request.browseKnowledge(this.cancelKnowlegdeDialog.bind(this), this.validateKnowlegdeDialog.bind(this))
    );
  }

  /**
   * Insert the selected pre-answered response
   * (simulate click on the eptica button)
   */
  validateKnowlegdeDialog(): void {
    this.doJsAction(['..'], request => request.validateKnowlegdeDialog());
  }

  /**
   * Close the knowledge dialog without inserting anything
   * (simulate click on the eptica button)
   */
  cancelKnowlegdeDialog() {
    this.doJsAction(['..'], request => request.cancelKnowlegdeDialog());
  }

  saveDraft(): void {
    this.doSubmitAction(request => request.saveDraft());
  }

  /**
   * Cancel the mail
   */
  giveUp(): void {
    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      if (contact.customerId) {
        this.outgoingMailDeprecatedService.deleteOpenedFromInteraction(contact.customerId);
      }

      if (this.mode === 'EDIT') {
        this.endContact(this.responseId, Option.of(RefuseReason.CANCELED_MAIL));
      } else {
        this.mainPromise().then(request => {
          if (!request.isMessageTextEmpty()) {
            this.doSubmitAction(request => request.cancelDraft(), RefuseReason.CANCELED_MAIL).finally(() => {
              this.doJsAction(['../..'], request => {
                this.endContact(this.responseId, Option.of(RefuseReason.CANCELED_MAIL));
                request.finish();
                return true;
              });
            });
          } else {
            this.doJsAction(['../..'], request => {
              this.endContact(this.responseId, Option.of(RefuseReason.CANCELED_MAIL));
              request.finish();
              return true;
            });
          }
        });
      }
    });
  }

  openSMSForm(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      contact.customerId && this.smsDeprecatedService.openSMSForContact(contact.customerId);
    });
  }

  openAppointmentForm(): void {
    this.contactDeprecatedService.getSelectedContact().then(contact => {
      this.contactDeprecatedService.getCustomer(contact.responseId).then(customer => {
        let firstName: string;
        let lastName: string;
        if (!!customer) {
          firstName = customer.firstname;
          lastName = customer.lastname;
        } else {
          firstName = contact.customerName;
          lastName = '';
        }
        this.appointmentDeprecatedService.openAppointmentForm(contact.responseId, contact.phoneNumber, firstName, lastName, contact.skills);
      });
    });
  }

  validate(): void {
    this.outgoingMailDeprecatedService.performSearchContacts();
  }

  cancelOutgoingMailContactSelection(): void {
    this.outgoingMailDeprecatedService.cancelOutgoingMailContactSelection();
  }

  canOpenOutcall(): boolean {
    return this.unitOutgoingCallDeprecatedService.canOpenOutcall();
  }

  outcallDisabled(): boolean {
    return this.unitOutgoingCallDeprecatedService.outcallDisabled();
  }

  /**
   * Open the Call component
   */
  openOutcall() {
    this.openOutcallAction();
  }

  mainPromise(): IPromise<IEpticaOutgoingFrame> {
    return this.outgoingMailDeprecatedService.getEpticaRequest();
  }

  checkEpticaDataAfterReload(request: IEpticaOutgoingFrame, epticaData: EpticaData, refuseReason?: Option<string>): IPromise<EpticaData> {
    if (epticaData.pageName === HOME_PAGE_NAME) {
      if (refuseReason && RefuseReason.CANCELED_MAIL === refuseReason.getOrNull()) {
        this.epticaDeprecatedService.closeOutgoingForm().finally(() => request.finish());
      } else {
        return this.doPostSubmitActions(request, epticaData)
          .finally(() => this.epticaDeprecatedService.closeOutgoingForm().finally(() => request.finish()))
          .finally(() => epticaData);
      }
    } else if (epticaData.pageName === REQUEST_CREATE_PAGE_NAME) {
      // Nothing to do, probably a server side validation error from eptica
      this.showActions = true;
      // Show the request
      request.show();
    } else {
      this.contactDeprecatedService.cancel(this.responseId).finally(() => this.mainPromise().then(r => r.finish()));
    }
    return this.$q.resolve(epticaData);
  }

  /**
   * Search for the Id of the created request, store it in the related contact entry
   * */
  doPostSubmitActions(request: IEpticaOutgoingFrame, epticaData: EpticaData): IPromise<EpticaData> {
    return EpticaHelper.delay(this.$q, this.$timeout, 2000, epticaData).then(() =>
      optToPromise(this.epticaDeprecatedService.getCurrentEpticaCsr(), this.$q).then(accountId =>
        this.epticaDeprecatedService.searchLastMailRequestSend(+accountId).then(
          esReqId => {
            request.setRealId(esReqId);
            this.contactDeprecatedService.updateRequestId(this.responseId, esReqId);
            return this.outgoingMailDeprecatedService.linkRequestIdToContactEntry(this.responseId, esReqId).then(() => epticaData);
          },
          error => {
            this.$log.error(`Cannot retrieve the eptica request id for the outgoing mail : ${error}`);
            return epticaData;
          }
        )
      )
    );
  }

  /**
   * Search for the Id of the created request, store it in the related contact entry
   * */
  protected override doPostJsonActions(
    request: IEpticaOutgoingFrame,
    epticaJsonResponse: IEpticaJsonResponse
  ): IPromise<IEpticaJsonResponse> {
    return EpticaHelper.delay(this.$q, this.$timeout, 2000, epticaJsonResponse).then(() =>
      optToPromise(this.epticaDeprecatedService.getCurrentEpticaCsr(), this.$q).then(accountId =>
        this.epticaDeprecatedService.searchLastMailRequestSend(+accountId).then(
          esReqId => {
            request.setRealId(esReqId);
            this.contactDeprecatedService.updateRequestId(this.responseId, esReqId);
            return this.outgoingMailDeprecatedService.linkRequestIdToContactEntry(this.responseId, esReqId).then(() => epticaJsonResponse);
          },
          error => {
            this.$log.error(`Cannot retrieve the eptica request id for the outgoing mail : ${error}`);
            return epticaJsonResponse;
          }
        )
      )
    );
  }

  private openOutcallAction(): void {
    if (!this.outcallRequested) {
      this.retrieveCustomerCardWithResponseId()
        .then(card => this.unitOutgoingCallDeprecatedService.openUnitOutgoingCallForm(card))
        .then(() => this.unitOutgoingCallDeprecatedService.finishAndClosePauseAndTask());
    }
  }

  private retrieveCustomerCardWithResponseId(): IPromise<Customer> {
    let deferred = this.$q.defer<Customer>();
    this.contactDeprecatedService.getCustomer(this.responseId).then(
      customer => {
        if (!!customer) {
          deferred.resolve(customer);
        } else {
          deferred.reject();
        }
      },
      () => deferred.reject()
    );
    return deferred.promise;
  }
}

export class MailOutgoingSelectionActions implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    responseId: '<',
  };
  controller = [
    '$log',
    '$q',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'smsDeprecatedService',
    'appointmentDeprecatedService',
    'toastDeprecatedService',
    'outgoingMailDeprecatedService',
    'unitOutgoingCallDeprecatedService',
    '$timeout',
    'mailFacade',
    'contactFacade',
    'mailService',
    'agentDesktopRouter',
    'translateService',
    MailOutgoingSelectionActionsCtrl,
  ];
}

export default 'contactMailOutgoingSelectionActions';
